import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { motion, useReducedMotion } from 'framer-motion';
import { Dumbbell, ShieldCheck, Ship, Landmark, Building2 } from 'lucide-react';

const portfolioBadges = [
    { icon: Dumbbell, label: 'fitness' },
    { icon: ShieldCheck, label: 'insurance' },
    { icon: Ship, label: 'import/export' },
    { icon: Landmark, label: 'banking' },
    { icon: Building2, label: 'enterprise' },
];

const itemVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut' } },
};

const badgeStaggerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.1 } },
};

const badgeItemVariants = {
    hidden: { opacity: 0, y: 16 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut' } },
};

function AboutUs() {
    const shouldReduceMotion = useReducedMotion();

    return (
        <motion.div
            initial={shouldReduceMotion ? false : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={shouldReduceMotion ? {} : { opacity: 0, y: -10 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
        >
            <Container fluid className="limited-width bg ogs-pri-text text-center py-5 mb-5">
                <h1 className="ogs-sec-text">About Us</h1>
            </Container>

            <motion.div
                variants={shouldReduceMotion ? {} : itemVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-80px' }}
            >
                <Container fluid className="limited-width bg ogs-pri-text text-center py-5 mb-5">
                    <p>
                        Welcome to <b className="ogs-high-text">Omni Group Services</b> – or OG Services for short. We are a multi-service technology and business solutions company that helps organisations solve real problems with practical, modern tools.
                    </p>
                </Container>
            </motion.div>

            <motion.div
                variants={shouldReduceMotion ? {} : itemVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-80px' }}
            >
                <Container fluid className="limited-width-wide bg ogs-pri-text py-5 mb-5">
                    <Row className="align-items-center g-4">
                        <Col md={7} className="text-start">
                            <p className="mb-0">
                                Our journey started with a single developer armed with roughly 10 years of battle-tested experience in the wild world of software.
                                Since then, we’ve grown into a powerhouse with skills spanning everything from fitness apps that make you sweat 💪 (in a good way),
                                to insurance systems that keep you covered 🛡️, to global import-export platforms that move mountains (and containers) 🌍🚢,
                                and even business transactional banking that keeps the money flowing 💸.
                                In short, we do it all — from sleek websites to complex enterprise solutions — and we do it with style. ✨
                            </p>
                        </Col>
                        <Col md={5} className="ogs-about-image">
                            <img
                                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=800&q=80"
                                alt="Team collaborating in a warm office"
                            />
                        </Col>
                    </Row>
                </Container>
            </motion.div>

            <motion.div
                className="limited-width d-flex flex-wrap justify-content-center gap-2 mb-5"
                variants={shouldReduceMotion ? {} : badgeStaggerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-80px' }}
            >
                {portfolioBadges.map(({ icon: Icon, label }) => (
                    <motion.span
                        key={label}
                        className="ogs-portfolio-badge"
                        variants={shouldReduceMotion ? {} : badgeItemVariants}
                        whileHover={{ scale: 1.05 }}
                    >
                        <Icon size={16} />
                        {label}
                    </motion.span>
                ))}
            </motion.div>

            <motion.div
                variants={shouldReduceMotion ? {} : itemVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-80px' }}
            >
                <Container fluid className="limited-width-wide bg ogs-pri-text py-5 mb-5">
                    <Row className="align-items-center g-4">
                        <Col md={5} className="ogs-about-image order-md-1">
                            <img
                                src="https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=800&q=80"
                                alt="Developer working at a laptop with code on screen"
                            />
                        </Col>
                        <Col md={7} className="text-start order-md-2">
                            <p className="mb-0">
                                We thrive on challenges. Let’s be honest, coding the same thing over and over is about as exciting as watching paint dry 🎨😴.
                                But every business has its own quirky rules and puzzles 🧩, and that’s what keeps our brains buzzing 🧠 and our coffee cups full ☕.
                                Because if everything was easy all the time, who’d be jazzed to roll out of bed and hit the keyboard each morning? ⏰💻
                            </p>
                        </Col>
                    </Row>
                </Container>
            </motion.div>

            <motion.div
                variants={shouldReduceMotion ? {} : itemVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-80px' }}
            >
                <Container fluid className="limited-width bg ogs-pri-text text-center py-5 mb-5">
                    <p>
                        But wait, there’s more! We’re not just code monkeys 🐒 chained to our desks. We foster a collaborative, learning-driven environment where no
                        industry is off-limits. Need us to dive into something new? Bring it on! We’re not afraid to get our hands dirty 🛠️ — whether it’s debugging
                        tricky software or assembling your office furniture (okay, maybe not the furniture 🪑, but we’re close).
                    </p>
                </Container>
            </motion.div>

            <motion.div
                variants={shouldReduceMotion ? {} : itemVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-80px' }}
            >
                <Container fluid className="limited-width bg ogs-pri-text py-5 mb-5 ogs-cta-block">
                    <p>
                        At OGS, we want to be challenged, pushed, and tested. So if you’ve got a tough problem, or even just need someone to help you pick out a
                        desk chair, we’re your go-to crew. 🤝
                    </p>
                </Container>
            </motion.div>
        </motion.div>
    );
}

export default AboutUs;
